import type { RiaBrandingProfile, RiaProfile } from '@/api/custom';

const SIGNUP_BRAND_CONTEXT_KEY = 'wavvest.signupBrandContext';
const PENDING_SIGNUP_INVITE_KEY = 'wavvest.pendingSignupInvite';
const PENDING_SIGNUP_INVITE_TTL_MS = 15 * 60 * 1000;
const BRAND_ID_PREFIX = 'brand-';
const BRANDED_LOGIN_FIRM_ROUTE_ID_KEY = 'wavvest.brandedLoginFirmRouteId';

export type SignupBrandContext = {
  id: string;
  brand_id: string;
  alias?: string;
  name: string;
  introduction: string;
  logo_uri: string;
  logo_light_uri?: string;
  text_logo_uri: string;
  light_text_logo_uri?: string;
  logo_mark_uri: string;
  compact_logo_uri?: string;
  compact_logo_light_uri?: string;
  background_uri: string;
  landing_color: string;
  co_branding: boolean;
  default_theme?: 'light' | 'dark';
};

type StoredSignupBrandContext = {
  profile: SignupBrandContext;
};

type PendingSignupInvite = {
  email: string;
  inviteToken: string;
};

type StoredPendingSignupInvite = PendingSignupInvite & {
  expiresAt: number;
};

export const getBrandValue = (value?: string | null): string =>
  typeof value === 'string' ? value.trim() : '';

export const getBrandIdFromFirmRouteId = (firmId?: string | null): string => {
  const normalizedFirmId = getBrandValue(firmId);
  if (!normalizedFirmId) {
    return '';
  }

  return normalizedFirmId.startsWith(BRAND_ID_PREFIX)
    ? normalizedFirmId
    : `${BRAND_ID_PREFIX}${normalizedFirmId}`;
};

export const getFirmRouteIdFromBrandId = (brandId?: string | null): string => {
  const normalizedBrandId = getBrandValue(brandId);
  if (!normalizedBrandId) {
    return '';
  }

  return normalizedBrandId.startsWith(BRAND_ID_PREFIX)
    ? normalizedBrandId.slice(BRAND_ID_PREFIX.length)
    : normalizedBrandId;
};

export const getFirmRouteIdFromBrandProfile = (
  profile:
    | Pick<RiaProfile | RiaBrandingProfile | SignupBrandContext, 'alias' | 'brand_id'>
    | null
    | undefined,
): string => {
  const alias = getBrandValue(profile?.alias);
  return alias || getFirmRouteIdFromBrandId(profile?.brand_id);
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isBrandIdShapedRouteSegment = (firmRouteId?: string | null): boolean => {
  const normalized = getBrandValue(firmRouteId);
  if (!normalized) {
    return false;
  }
  const withoutPrefix = normalized.startsWith(BRAND_ID_PREFIX)
    ? normalized.slice(BRAND_ID_PREFIX.length)
    : normalized;
  return UUID_PATTERN.test(withoutPrefix);
};

export const getSignupBrandContextFromProfile = (
  profile: RiaProfile | RiaBrandingProfile | SignupBrandContext | null | undefined,
): SignupBrandContext | null => {
  if (!profile?.co_branding) {
    return null;
  }

  return {
    id: getBrandValue(profile.id),
    brand_id: getBrandValue(profile.brand_id),
    alias: getBrandValue(profile.alias) || undefined,
    name: getBrandValue(profile.name),
    introduction: getBrandValue(profile.introduction),
    logo_uri: getBrandValue(profile.logo_uri),
    logo_light_uri: getBrandValue(profile.logo_light_uri),
    text_logo_uri: getBrandValue(profile.text_logo_uri),
    light_text_logo_uri: getBrandValue(profile.light_text_logo_uri),
    logo_mark_uri: getBrandValue(profile.logo_mark_uri),
    compact_logo_uri: getBrandValue(profile.compact_logo_uri),
    compact_logo_light_uri: getBrandValue(profile.compact_logo_light_uri),
    background_uri: getBrandValue(profile.background_uri),
    landing_color: getBrandValue(profile.landing_color),
    co_branding: true,
    default_theme: profile.default_theme,
  };
};

export const clearSignupBrandContext = () => {
  try {
    localStorage.removeItem(SIGNUP_BRAND_CONTEXT_KEY);
  } catch (error) {
    console.error('Failed to clear signup brand context:', error);
  }
};

export const persistSignupBrandContext = (
  profile: RiaProfile | RiaBrandingProfile | SignupBrandContext | null | undefined,
) => {
  const brandContext = getSignupBrandContextFromProfile(profile);

  if (!brandContext) {
    clearSignupBrandContext();
    return;
  }

  try {
    const storedContext: StoredSignupBrandContext = {
      profile: brandContext,
    };
    localStorage.setItem(SIGNUP_BRAND_CONTEXT_KEY, JSON.stringify(storedContext));
  } catch (error) {
    console.error('Failed to persist signup brand context:', error);
  }
};

export const readSignupBrandContext = (): SignupBrandContext | null => {
  try {
    const storedValue = localStorage.getItem(SIGNUP_BRAND_CONTEXT_KEY);

    if (!storedValue) {
      return null;
    }

    const storedContext = JSON.parse(storedValue) as Partial<StoredSignupBrandContext>;

    if (!storedContext.profile) {
      clearSignupBrandContext();
      return null;
    }

    return getSignupBrandContextFromProfile(storedContext.profile);
  } catch (error) {
    console.error('Failed to read signup brand context:', error);
    clearSignupBrandContext();
    return null;
  }
};

export const persistPendingSignupInvite = ({ email, inviteToken }: PendingSignupInvite) => {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedInviteToken = inviteToken.trim();

  if (!normalizedEmail || !normalizedInviteToken) {
    return;
  }

  try {
    const storedInvite: StoredPendingSignupInvite = {
      email: normalizedEmail,
      inviteToken: normalizedInviteToken,
      expiresAt: Date.now() + PENDING_SIGNUP_INVITE_TTL_MS,
    };
    localStorage.setItem(PENDING_SIGNUP_INVITE_KEY, JSON.stringify(storedInvite));
  } catch (error) {
    console.error('Failed to persist pending signup invite:', error);
  }
};

export const readPendingSignupInviteToken = (email: string): string => {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    return '';
  }

  try {
    const storedValue = localStorage.getItem(PENDING_SIGNUP_INVITE_KEY);
    if (!storedValue) {
      return '';
    }

    const storedInvite = JSON.parse(storedValue) as Partial<StoredPendingSignupInvite>;
    if (typeof storedInvite.expiresAt !== 'number' || storedInvite.expiresAt <= Date.now()) {
      clearPendingSignupInvite();
      return '';
    }

    if (
      storedInvite.email?.trim().toLowerCase() !== normalizedEmail ||
      !storedInvite.inviteToken?.trim()
    ) {
      return '';
    }

    return storedInvite.inviteToken.trim();
  } catch (error) {
    console.error('Failed to read pending signup invite:', error);
    return '';
  }
};

export const clearPendingSignupInvite = () => {
  try {
    localStorage.removeItem(PENDING_SIGNUP_INVITE_KEY);
  } catch (error) {
    console.error('Failed to clear pending signup invite:', error);
  }
};

export const persistBrandedLoginFirmRouteId = (firmRouteId?: string | null) => {
  const normalizedFirmRouteId = getBrandValue(firmRouteId);
  if (!normalizedFirmRouteId) {
    return;
  }

  try {
    localStorage.setItem(BRANDED_LOGIN_FIRM_ROUTE_ID_KEY, normalizedFirmRouteId);
  } catch (error) {
    console.error('Failed to persist branded login firm route id:', error);
  }
};

export const readBrandedLoginFirmRouteId = (): string => {
  try {
    const storedValue = localStorage.getItem(BRANDED_LOGIN_FIRM_ROUTE_ID_KEY);
    return getBrandValue(storedValue);
  } catch (error) {
    console.error('Failed to read branded login firm route id:', error);
    return '';
  }
};

export const clearBrandedLoginFirmRouteId = () => {
  try {
    localStorage.removeItem(BRANDED_LOGIN_FIRM_ROUTE_ID_KEY);
  } catch (error) {
    console.error('Failed to clear branded login firm route id:', error);
  }
};

export const getBrandedLoginPath = (
  notice?: string,
  searchParams?: URLSearchParams | string,
): string => {
  const firmRouteId = readBrandedLoginFirmRouteId();
  const params = new URLSearchParams(searchParams ?? '');
  if (notice) {
    params.set('notice', notice);
  }
  const queryString = params.toString();
  const basePath = firmRouteId ? `/firm/${encodeURIComponent(firmRouteId)}` : '/';
  return queryString ? `${basePath}?${queryString}` : basePath;
};
